// src/cache/indexeddb.ts
var DB_VERSION = 1;
function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
var IndexedDBCacheAdapter = class {
  dbName;
  storeName;
  /** Resolved after the first operation attempt. null if open failed. */
  dbPromise = null;
  constructor(dbName = "rtvloco-user-preferences", storeName = "preferences") {
    this.dbName = dbName;
    this.storeName = storeName;
  }
  // -------------------------------------------------------------------------
  // Lazy DB initialization
  // -------------------------------------------------------------------------
  /**
   * Returns the open IDBDatabase, lazily initializing it on the first call.
   * If the database fails to open, resolves with `null` to trigger degraded mode.
   */
  getDb() {
    if (this.dbPromise !== null) {
      return this.dbPromise;
    }
    this.dbPromise = new Promise((resolve) => {
      let request;
      try {
        request = indexedDB.open(this.dbName, DB_VERSION);
      } catch {
        resolve(null);
        return;
      }
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
    return this.dbPromise;
  }
  // -------------------------------------------------------------------------
  // CacheAdapter implementation
  // -------------------------------------------------------------------------
  async get(key) {
    const db = await this.getDb();
    if (db === null) return null;
    try {
      const tx = db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      const result = await promisifyRequest(store.get(key));
      return result ?? null;
    } catch {
      return null;
    }
  }
  async set(key, entry) {
    const db = await this.getDb();
    if (db === null) return;
    try {
      const tx = db.transaction(this.storeName, "readwrite");
      const store = tx.objectStore(this.storeName);
      await promisifyRequest(store.put(entry, key));
    } catch {
    }
  }
  async delete(key) {
    const db = await this.getDb();
    if (db === null) return;
    try {
      const tx = db.transaction(this.storeName, "readwrite");
      const store = tx.objectStore(this.storeName);
      await promisifyRequest(store.delete(key));
    } catch {
    }
  }
  async clear() {
    const db = await this.getDb();
    if (db === null) return;
    try {
      const tx = db.transaction(this.storeName, "readwrite");
      const store = tx.objectStore(this.storeName);
      await promisifyRequest(store.clear());
    } catch {
    }
  }
  async keys() {
    const db = await this.getDb();
    if (db === null) return [];
    try {
      const tx = db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      const rawKeys = await promisifyRequest(store.getAllKeys());
      return rawKeys;
    } catch {
      return [];
    }
  }
  /**
   * Closes the underlying IDBDatabase connection and resets the lazy handle
   * so the next operation will re-open the database. Useful for cleanup in
   * tests and during application teardown.
   */
  async close() {
    const db = await this.dbPromise;
    if (db !== null) {
      db.close();
    }
    this.dbPromise = null;
  }
};

// src/cache/adapter.ts
function deterministicStringify(value) {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    const items = value.map((item) => deterministicStringify(item));
    return `[${items.join(",")}]`;
  }
  const record = value;
  const sortedKeys = Object.keys(record).sort();
  const pairs = sortedKeys.map(
    (key) => `${JSON.stringify(key)}:${deterministicStringify(record[key])}`
  );
  return `{${pairs.join(",")}}`;
}
function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
function computeChecksum(data) {
  const canonical = deterministicStringify(data);
  return simpleHash(canonical);
}
function isEntryValid(entry) {
  if (entry.expiresAt !== null) {
    const now = Date.now();
    const expiryMs = new Date(entry.expiresAt).getTime();
    if (expiryMs <= now) {
      return false;
    }
  }
  const expectedChecksum = computeChecksum(entry.document.data);
  return entry.checksum === expectedChecksum;
}
function createCacheEntry(document, ttlMs) {
  const now = /* @__PURE__ */ new Date();
  const cachedAt = now.toISOString();
  const expiresAt = ttlMs !== void 0 ? new Date(now.getTime() + ttlMs).toISOString() : null;
  const checksum = computeChecksum(document.data);
  return Object.freeze({
    document,
    cachedAt,
    expiresAt,
    checksum
  });
}

export {
  IndexedDBCacheAdapter,
  computeChecksum,
  isEntryValid,
  createCacheEntry
};
//# sourceMappingURL=chunk-WXAGNOHO.js.map